import React, { useRef, useLayoutEffect, useState } from 'react'
import ResizeObserver from 'resize-observer-polyfill'
import {
  BreadcrumbDivider,
  BreadcrumbWrapper,
  BreadcrumbItem,
} from './HeaderNavigation.styles'

const OFFSET = 32

/**
 * This hook will track the breadcrumbs don't fit into the container width
 * and so they should be collapsed into an ellipsis with tooltip
 * @returns [ref for the container, ref for the chidlren, indexes of the items that don't fit, starting from the leftmost]
 */
export function useTrackCollapsableBreadcrumbs<T extends HTMLElement>(): [
  React.RefObject<T>,
  <ChildT extends HTMLElement>(index: number) => React.RefCallback<ChildT>,
  Set<number>
] {
  const containerRef = useRef<T>(null)
  const childrenRef = useRef<
    Record<number, { index: number; element: HTMLElement }>
  >({})
  const childrenWidthsRef = useRef<number[] | null>(null)
  const totalChildrenWidthRef = useRef(0)
  const [collapsableItems, setCollapsableItems] = useState<Set<number>>(
    new Set([])
  )

  // The index is needed to understand the order of the items in the container
  const addChildRef = <El extends HTMLElement>(index: number) => (
    element: El | null
  ) => {
    element && (childrenRef.current[index] = { index, element })
  }

  useLayoutEffect(() => {
    if (!containerRef.current) return

    // Store the initial children widths and the totalWidth
    // to avoid recalculations during the container resize
    if (!childrenWidthsRef.current) {
      childrenWidthsRef.current = Object.values(childrenRef.current)
        .sort((a, b) => a.index - b.index) // order by index, to make sure that the array follows the html order
        .map(item => {
          totalChildrenWidthRef.current += item.element.clientWidth
          return item.element.clientWidth
        })
    }

    function checkCollapsableItems() {
      const container = containerRef.current
      const childrenWidths = childrenWidthsRef.current
      const totalChildrenWidth = totalChildrenWidthRef.current

      if (!container || !childrenWidths) return

      let currentWidth = totalChildrenWidth
      let i = 0
      const collapsableItems = new Set<number>()

      // We check if all the items fit into the container client width
      // if not, we remove leftmost item width, and check again.
      while (
        currentWidth > container.clientWidth &&
        i < childrenWidths.length
      ) {
        // the offset is useful to collapse the items a little bit before they reach the container boundaries
        currentWidth -= childrenWidths[i] - OFFSET
        collapsableItems.add(i)
        i += 1
      }

      setCollapsableItems(collapsableItems)
    }

    const observer = new ResizeObserver(checkCollapsableItems)
    observer.observe(containerRef.current)

    return () => observer.disconnect()
  }, [])

  return [containerRef, addChildRef, collapsableItems]
}

/**
 * This component will add a divider between each breadcrumb
 * and collapse the items that don't fit into the container
 *
 * When an item doesn't fit anymore into the container
 * The component will pass a collapse:boolean prop to
 * the item, and it will be up to the item to handle
 * how to collapse the content.
 *
 * In HeaderNavigation, all the children accepts a
 * collapse:boolean prop, and when it's true, they will
 * show an ellipsis with a tooltip, instead of the full text
 */
export const Breadcrumbs: React.FC = ({ children }) => {
  const [
    containerRef,
    breadcrumbRef,
    collapsableBreadcrumbs,
  ] = useTrackCollapsableBreadcrumbs<HTMLDivElement>()

  // Removing invalid children like `false`, `null`, and `undefined`
  const validChildren = React.Children.toArray(children).filter(
    Boolean
  ) as JSX.Element[]

  return (
    <BreadcrumbWrapper ref={containerRef}>
      {validChildren.map((child, i) => (
        // Cloning the element to pass the collapse prop
        // The clone is not expensive, since we have max 4 items in the breadcrumbs
        <BreadcrumbItem key={i} ref={breadcrumbRef(i)}>
          {React.cloneElement(child, {
            collapse: collapsableBreadcrumbs.has(i),
          })}

          {i < validChildren.length - 1 && <BreadcrumbDivider />}
        </BreadcrumbItem>
      ))}
    </BreadcrumbWrapper>
  )
}
